import { cloneDeep, isEmpty, isEqual } from 'lodash'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { computed, ref } from 'vue'
import {
	useAlertStore,
	useBackendStore,
	useThemeStore,
	useUserStore,
} from '../system'
import {
	mailTemplateService,
	notificationService,
	settingsService,
} from '@/services/api'
import type { Url } from '../system/backend'

type Status = 'initial' | 'loading' | 'error' | 'ready' | 'update'

export const useSettingStore = defineStore('setting', () => {
	const status = ref<Status>('initial')
	const data = ref<Api.ClientSettingFull>()
	const dataInit = ref<Api.ClientSettingFull>()
	const mailLogo = ref<ArrayBuffer>()
	const mailTemplates = ref<Api.MailTemplate[]>([])
	const mailTemplatesInit = ref<Api.MailTemplate[]>([])

	const getBuildingView = computed(() => (data.value?.objects_views ? 1 : 0))
	const getBuildingInfoHidden = computed(
		() => data.value?.hide_object_info || false
	)
	const getMeterReadingHidden = computed(
		() => data.value?.hide_meter_reading || false
	)
	const getSyncUpdatePending = computed(
		() => data.value?.sync_update_pending || false
	)
	const getDataInit = computed(() => dataInit.value)
	const getData = computed(() => data.value)
	const getMaillogo = computed(() => mailLogo.value)
	const getMailtemplates = computed(() => mailTemplates.value)
	const getMailtemplatesInit = computed(() => mailTemplatesInit.value)
	const getStatus = computed(() => status.value)
	// const getField

	function setData(value) {
		data.value = value
	}
	function setMaillogo(value: ArrayBuffer | undefined) {
		mailLogo.value = value
	}
	function setMailtemplates(value) {
		mailTemplates.value = value
	}
	function setMailtemplatesInit(value) {
		mailTemplatesInit.value = value
	}
	function setDataInit(value: Api.ClientSettingFull) {
		dataInit.value = value
	}
	function setStatus(value: Status) {
		status.value = value
	}
	// function updateField() {} //TODO

	function load(force = false) {
		setStatus('loading')
		if (!isEmpty(data.value) && (typeof force === 'undefined' || !force)) {
			setStatus('ready')
			return Promise.resolve(data.value)
		}
		// if (Vue.$sessionStorage.hasKey('settings') && !force) {
		// 	let settings = Vue.$sessionStorage.get('settings')
		// 	commit('setDataInit', cloneDeep(settings))
		// 	commit('setData', settings)
		// 	commit('setDashboardElements', settings.dashboard_elements)
		// 	commit('setStatus', 'ready')
		// 	return Promise.resolve(settings)
		// } // TODO
		const db = useUserStore().db(useBackendStore().url as Url)
		return settingsService.get(db.id).then(
			(settings) => {
				if (!settings) return
				setDataInit(settings)
				setData(settings)
				// Vue.$sessionStorage.set('settings', settings)
				setStatus('ready')
				return Promise.resolve()
			},
			(error) => {
				useAlertStore().errorAlert(error)
				return Promise.reject(error)
			}
		)
	}
	async function loadLogo({ id, type = 'logo' }) {
		const logo = type === 'logo' ? useThemeStore().getLogo : getMaillogo.value
		if (!isEmpty(logo)) {
			return logo
		}
		setStatus('loading')
		return settingsService.getLogo({ id, type }).then(
			(response) => {
				const logo = response.data
				if (type === 'logo') {
					useThemeStore().setLogo({ logo })
				} else {
					setMaillogo(logo)
				}
				setStatus('ready')
				return logo
			},
			(error) => {
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}
	function loadMailTemplates() {
		if (mailTemplates.value.length > 0) {
			return mailTemplates.value
		}
		status.value = 'loading'
		return mailTemplateService.index().then(
			({ data }) => {
				mailTemplates.value = data.data
				mailTemplatesInit.value = data.data
				setStatus('ready')
				return data.data
			},
			(err) => {
				useAlertStore().errorAlert(err)
				return err
			}
		)
	}
	function previewMail({ name, params }: Record<string, string>) {
		setStatus('loading')

		return new Promise((resolve, reject) => {
			notificationService.preview({ name, params }).then(
				(preview) => {
					setStatus('ready')
					resolve(preview)
				},
				(error) => {
					useAlertStore().errorAlert(error)
					reject(error)
				}
			)
		})
	}
	function updateMailTemplates() {
		return new Promise((resolve) => {
			const mTemplates = cloneDeep(mailTemplates.value)
			const mTemplatesInit = cloneDeep(mailTemplatesInit.value)
			if (isEqual(mTemplates, mTemplatesInit)) {
				resolve('')
			}
			setStatus('update')
			for (let i = 0; i < mTemplates.length; i++) {
				if (isEqual(mTemplates[i], mTemplatesInit[i])) {
					continue
				}
				const { id } = mTemplates[i]
				for (const key in mTemplates[i]) {
					if (typeof mTemplates[i][key] === 'string') {
						mTemplates[i][key] = mTemplates[i][key].replaceAll('&amp;', '&')
					}
				}
				mailTemplateService.update(id, { data: mTemplates[i] }).then(
					({ data }) => {
						mTemplates[i] = data
						mTemplatesInit[i] = data
						mailTemplates.value = mTemplates
						mailTemplatesInit.value = mTemplatesInit
						status.value = 'ready'
						resolve(data)
					},
					(error) => {
						useAlertStore().errorAlert(error)
						return error
					}
				)
			}
		})
	}
	function update({ fields }) {
		const data = cloneDeep(getData.value)
		const formData = new FormData() //TODO should be removable https://axios-http.com/docs/multipart
		for (const prop in data) {
			if (
				(Object.prototype.hasOwnProperty.call(data, prop) &&
					typeof fields === 'object' &&
					Object.keys(fields).includes(prop)) ||
				(Array.isArray(fields) && fields.includes(prop))
			) {
				if (
					(prop === 'logo' || prop === 'maillogo') &&
					typeof data[prop] === 'boolean'
				) {
					continue
				}
				if (
					data[prop] === null ||
					typeof data[prop] === 'undefined' ||
					(typeof data[prop] === 'string' && data[prop].length === 0)
				) {
					formData.append(prop, '')
				} else {
					formData.append(prop, data[prop])
				}
			}
		}

		status.value = 'update'
		return settingsService
			.update(data.id, {
				data: formData,
				settings: {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				},
			})
			.then(
				(response) => {
					setData(response.data)
					// Vue.$localStorage.set('settings', settings) // TODO
					return useThemeStore()
						.load()
						.then(() => {
							setStatus('ready')
							return response.data
						})
				},
				(error) => {
					useAlertStore().errorAlert(error)
					return error
				}
			)
	}
	function removeLogo({ id, type = 'logo' }) {
		setStatus('loading')
		return settingsService.deleteLogo({ id, type }).then(
			() => {
				// const settings = Vue.$localStorage.get('settings')//TODO
				// settings[type] = false
				// Vue.$localStorage.set('settings', settings)
				if (type === 'logo') {
					useThemeStore().setLogo(null)
				} else {
					setMaillogo(undefined)
				}
				setStatus('ready')
			},
			(error) => {
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}

	return {
		status,
		data,
		dataInit,
		mailLogo,
		mailTemplates,
		mailTemplatesInit,
		getBuildingView,
		getBuildingInfoHidden,
		getMeterReadingHidden,
		getSyncUpdatePending,
		getDataInit,
		getData,
		getMaillogo,
		getMailtemplates,
		getMailtemplatesInit,
		getStatus,
		setData,
		setMaillogo,
		setMailtemplates,
		setMailtemplatesInit,
		setDataInit,
		setStatus,
		load,
		loadLogo,
		loadMailTemplates,
		previewMail,
		updateMailTemplates,
		update,
		removeLogo,
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useSettingStore, import.meta.hot))
}
